//Libs
import {
  Fragment,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

//Local
import { ExistingMembershipTemplate } from "../../models/membership-template";
import * as strings from "../../strings";
import SearchBox from "../../components/SearchBox";
import currencyFormatter from "../../currency";

interface Props {
  membershipTemplateList: ExistingMembershipTemplate[];
  currency: string;
  onEditTemplate: (membershipTemplate: ExistingMembershipTemplate) => void;
  addTemplateButton: React.ReactNode;
}

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
// This is for tables that use percentage-based heights - ie `h-full min-h-[500px]`
import "../../assets/css/ag-grid-inject-height.css";
import HeadingOne from "../../components/HeadingOne";

export default function MembershipTemplateListPage({
  membershipTemplateList,
  currency,
  onEditTemplate,
  addTemplateButton,
}: Props) {
  return (
    <Fragment>
      <HeadingOne className="mb-8">{strings.MEMBERSHIP_TEMPLATES}</HeadingOne>
      <MembershipTemplateListTable
        membershipTemplateList={membershipTemplateList}
        currency={currency}
        addTemplateButton={addTemplateButton}
        onEditTemplate={onEditTemplate}
      />
    </Fragment>
  );
}

const commonColProps = {
  minWidth: 150,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass:
    "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
  flex: 1,
};

// interface MembershipTemplateListTableProps {
//   membershipTemplateList: ExistingMembershipTemplate[];
//   currency: string;
//   onEditMambershipTemplate: (
//     membershipTemplate: ExistingMembershipTemplate,
//   ) => void;
//   addNewMembershipTemplateButton: React.ReactNode;
// }

/* TABLE COMPONENT */
const MembershipTemplateListTable = memo(
  ({
    membershipTemplateList,
    currency,
    onEditTemplate: onEditMambershipTemplate,
    addTemplateButton: addNewMembershipTemplateButton,
  }: Props) => {
    const [gridReady, setGridReady] = useState(false);
    const gridRef = useRef<AgGridReact>(null);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    useLayoutEffect(() => {
      if (gridRef.current && gridReady) {
        gridRef.current.api?.sizeColumnsToFit();
      }
    }, [gridReady]);

    const onFilterTextBoxChanged = useCallback(() => {
      if (gridRef.current) {
        gridRef.current.api.setGridOption(
          "quickFilterText",
          (document.getElementById("filter-text-box") as HTMLInputElement)
            .value,
        );
      }
    }, []);

    const renderPrice = useCallback(
      function (params: ICellRendererParams) {
        return <div>{currencyFormatter(params.data.price, currency)}</div>;
      },
      [currency],
    );

    const formattedText = (params: ICellRendererParams): JSX.Element => {
      return <div className="capitalize">{params.value}</div>;
    };

    const columnDefs: ColDef[] = [
      {
        headerName: "Title",
        field: "title",
        width: 200,
        cellRenderer: formattedText,
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return params.value;
        },
      },
      {
        field: "description",
        headerName: "Description",
        width: 200,
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        headerName: "Price",
        field: "price",
        cellRenderer: renderPrice,
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        headerName: "Discount %",
        field: "discount",
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        headerName: "Invoice Frequency",
        field: "invoiceFrequency",
        cellRenderer: formattedText,
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
    ];

    return (
      <Fragment>
        <div className="flex flex-col-reverse justify-between gap-6 xs:flex-row">
          <SearchBox
            onInput={onFilterTextBoxChanged}
            widthClasses="xs:max-w-sm grow"
          />
          <div className="shrink-0">{addNewMembershipTemplateButton}</div>
        </div>
        <div className="ag-theme-alpine h-[1000px] max-h-[68vh] pb-6">
          <AgGridReact
            ref={gridRef}
            onGridReady={onGridReady}
            defaultColDef={commonColProps}
            className="mt-5 shadow"
            rowData={membershipTemplateList}
            animateRows={true}
            rowSelection="single"
            cacheQuickFilter={true}
            columnDefs={columnDefs}
            onRowClicked={(event: RowClickedEvent) =>
              onEditMambershipTemplate(event.data)
            }
          />
        </div>
      </Fragment>
    );
  },
);
